import { FirebaseApp, getApp, getApps, initializeApp } from 'firebase/app'
import {
  Auth,
  browserPopupRedirectResolver,
  browserSessionPersistence,
  GoogleAuthProvider,
  initializeAuth,
  OAuthProvider,
} from 'firebase/auth'

const {
  REACT_APP_AUTH_PROVIDER,
  REACT_APP_FIREBASE_API_KEY,
  REACT_APP_FIREBASE_AUTH_DOMAIN,
  REACT_APP_FIREBASE_PROJECT_ID,
  REACT_APP_FIREBASE_APP_ID,
  REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
} = process.env

export type SocialProviderId = 'google' | 'apple'

export interface FirebaseClient {
  auth: Auth
  providers: Record<SocialProviderId, GoogleAuthProvider | OAuthProvider>
}

// REACT_APP_AUTH_PROVIDER switches the whole page between the legacy endpoints and Firebase Auth.
export function isFirebaseAuthEnabled(): boolean {
  return REACT_APP_AUTH_PROVIDER === 'firebase'
}

let firebaseClient: FirebaseClient | null = null

export function getFirebaseClient(): FirebaseClient {
  if (!firebaseClient) {
    const app: FirebaseApp = getApps().length
      ? getApp()
      : initializeApp({
          apiKey: REACT_APP_FIREBASE_API_KEY,
          authDomain: REACT_APP_FIREBASE_AUTH_DOMAIN,
          projectId: REACT_APP_FIREBASE_PROJECT_ID,
          appId: REACT_APP_FIREBASE_APP_ID,
          messagingSenderId: REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
        })

    // Session persistence only: this page signs a user in for a single OAuth request and must not
    // remember them for the next one, which may be for a different account.
    const auth = initializeAuth(app, {
      persistence: browserSessionPersistence,
      popupRedirectResolver: browserPopupRedirectResolver,
    })

    const googleProvider = new GoogleAuthProvider()
    googleProvider.setCustomParameters({ prompt: 'select_account' })

    const appleProvider = new OAuthProvider('apple.com')
    appleProvider.addScope('email')
    appleProvider.addScope('name')

    firebaseClient = {
      auth,
      providers: { google: googleProvider, apple: appleProvider },
    }
  }

  return firebaseClient
}
